import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MAX_APPOINTMENT_NOTES_LENGTH,
  MAX_APPOINTMENT_TYPE_LENGTH,
  endsAfterStarts,
  type CreateAppointmentRequest,
  type PatientResponse as Patient,
  type UpdateAppointmentRequest,
} from '@emr/shared';

import {
  appointmentErrorMessage,
  createAppointment,
  updateAppointment,
  type Appointment,
} from '../api/appointments.ts';
import { isContractMismatch } from '../api/client.ts';
import { Button, ErrorText, Field, SelectField, TextAreaField } from '../components/primitives.tsx';
import { FailureNotice } from '../components/AsyncState.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDate, formatTime } from '../format/formatDateTime.ts';
import { fromDatetimeLocalValue, toDatetimeLocalValue } from './datetimeLocalInput.ts';
import { PatientPicker } from './PatientPicker.tsx';
import { usePatient } from './usePatient.ts';
import { useOrgMembers } from './useOrgMembers.ts';

export interface AppointmentFormDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The provider whose schedule is currently being viewed — the default for a new booking. */
  readonly defaultProviderId: string;
  /** Present to reschedule/edit an existing appointment; absent to book a new one. `patientId` is
   * never editable here, matching `updateAppointmentRequestSchema`'s own "moving to a different
   * patient is a cancellation and a new booking, not an edit" reasoning.
   *
   * Typed `| undefined` explicitly, not just `?:` — `tsconfig.json`'s `exactOptionalPropertyTypes`
   * treats "key omitted" and "key present with value `undefined`" as different things, and the
   * caller's own state (`SchedulePage`'s `dialog.appointment`) is `Appointment | undefined`, passed
   * through as a prop rather than always omitted. */
  readonly appointment?: Appointment | undefined;
  readonly onSaved: (appointment: Appointment) => void;
}

interface FormState {
  readonly providerId: string;
  readonly appointmentType: string;
  readonly startsAt: string; // datetime-local value
  readonly endsAt: string; // datetime-local value
  readonly notes: string;
}

function initialFormState(defaultProviderId: string, appointment?: Appointment): FormState {
  if (appointment !== undefined) {
    return {
      providerId: appointment.providerId,
      appointmentType: appointment.appointmentType,
      startsAt: toDatetimeLocalValue(appointment.startsAt),
      endsAt: toDatetimeLocalValue(appointment.endsAt),
      notes: appointment.notes ?? '',
    };
  }
  return {
    providerId: defaultProviderId,
    appointmentType: '',
    startsAt: '',
    endsAt: '',
    notes: '',
  };
}

/**
 * Book a new appointment, or reschedule/edit an existing one — one dialog, two modes, because the
 * fields (provider, type, time range, notes) are identical; only the patient field and which API
 * call fires on submit differ.
 */
export function AppointmentFormDialog({
  open,
  onOpenChange,
  defaultProviderId,
  appointment,
  onSaved,
}: AppointmentFormDialogProps) {
  const { t } = useTranslation('schedule');
  const isEdit = appointment !== undefined;
  const queryClient = useQueryClient();
  const { preference } = useDateTimeFormat();
  const members = useOrgMembers();
  // A native `datetime-local` control always renders in the *browser's own* locale — it cannot be
  // re-skinned to honour `DateTimeFormatContext`'s per-user preference the way every other date/time
  // in this feature does. This is a real, visible inconsistency when a viewer's OS locale differs
  // from their in-app format choice, and there is no fix beyond replacing the native control with a
  // fully custom picker (out of scope here) — so the field's hint at least states what the app's own
  // preference would show, as a cross-check against whatever the input itself is rendering.
  const now = new Date().toISOString();
  const datetimeFormatHint = t('form.datetimeFormatHint', {
    date: formatDate(now, preference),
    time: formatTime(now, preference),
  });

  const [form, setForm] = useState<FormState>(() =>
    initialFormState(defaultProviderId, appointment),
  );
  const [patient, setPatient] = useState<Patient | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const existingPatient = usePatient(appointment?.patientId ?? null);

  // Re-seed the form each time the dialog opens for a (possibly different) appointment, or for a
  // fresh booking — otherwise closing and reopening would show whatever was left half-typed.
  // `seededFor` is cleared on close (not just compared against the next open's key) specifically so
  // that closing after a successful *create* and immediately opening a second fresh booking re-seeds
  // too — both have the same `'create'` key, so without clearing on close the second booking would
  // silently reopen holding the first one's already-submitted values.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const seedKey = appointment?.id ?? 'create';
  if (!open && seededFor !== null) {
    setSeededFor(null);
  } else if (open && seededFor !== seedKey) {
    setForm(initialFormState(defaultProviderId, appointment));
    setPatient(null);
    setFormError(null);
    setSeededFor(seedKey);
  }

  const create = useMutation({
    mutationFn: (input: CreateAppointmentRequest) => createAppointment(input),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onSaved(created);
      onOpenChange(false);
    },
  });

  const update = useMutation({
    mutationFn: (input: UpdateAppointmentRequest) => {
      // Reached only from the `appointment !== undefined` branch of `handleSubmit` below — this
      // check is a real runtime guard against that invariant slipping, not a type-checker
      // silencer, which is why it throws rather than using a non-null assertion here.
      if (appointment === undefined) {
        throw new Error('update mutation fired with no appointment loaded');
      }
      return updateAppointment(appointment.id, input);
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      onSaved(updated);
      onOpenChange(false);
    },
  });

  const pending = create.isPending || update.isPending;
  const mutationError = create.error ?? update.error;

  function handleSubmit() {
    setFormError(null);

    const providerId = form.providerId.trim();
    if (providerId.length === 0) {
      setFormError(t('form.errors.chooseProvider'));
      return;
    }
    const appointmentType = form.appointmentType.trim();
    if (appointmentType.length === 0) {
      setFormError(t('form.errors.enterAppointmentType'));
      return;
    }
    const startsAt = fromDatetimeLocalValue(form.startsAt);
    const endsAt = fromDatetimeLocalValue(form.endsAt);
    if (startsAt === null || endsAt === null) {
      setFormError(t('form.errors.enterStartAndEnd'));
      return;
    }
    // Reuses `@emr/shared`'s own `endsAfterStarts` rather than re-deriving the same comparison —
    // it's the identical rule `createAppointmentRequestSchema`/`updateAppointmentRequestSchema`
    // enforce at the server boundary, so a client-side re-implementation risked drifting from it.
    if (!endsAfterStarts(startsAt, endsAt)) {
      setFormError(t('form.errors.endAfterStart'));
      return;
    }

    const notes = form.notes.trim();

    if (appointment !== undefined) {
      update.mutate({
        providerId,
        appointmentType,
        startsAt,
        endsAt,
        notes: notes.length > 0 ? notes : null,
      });
      return;
    }

    if (patient === null) {
      setFormError(t('form.errors.choosePatient'));
      return;
    }
    create.mutate({
      patientId: patient.id,
      providerId,
      appointmentType,
      startsAt,
      endsAt,
      notes: notes.length > 0 ? notes : undefined,
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-panel border border-line bg-surface p-4 shadow-overlay sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="m-0 text-lg font-semibold text-ink">
              {isEdit ? t('form.titleReschedule') : t('form.titleBook')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="secondary" aria-label={t('form.close')}>
                {t('form.close')}
              </Button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            {isEdit ? t('form.descriptionEdit') : t('form.descriptionCreate')}
          </Dialog.Description>

          {isEdit ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-ink">{t('form.patientLabel')}</span>
              <p className="m-0 rounded-control border border-field-line bg-field px-3 py-2 text-sm text-ink">
                {existingPatient.data === undefined
                  ? existingPatient.isError
                    ? t('form.couldNotLoadPatient')
                    : t('form.loading')
                  : t('form.patientSummary', {
                      lastName: existingPatient.data.lastName,
                      firstName: existingPatient.data.firstName,
                      mrn: existingPatient.data.mrn,
                    })}
              </p>
            </div>
          ) : (
            <PatientPicker
              selected={patient}
              onSelect={setPatient}
              onClear={() => {
                setPatient(null);
              }}
            />
          )}

          {members.data === undefined ? (
            <p className="m-0 text-sm text-ink-muted">
              {members.isError ? t('page.couldNotLoadProviders') : t('page.loadingProviders')}
            </p>
          ) : (
            <SelectField
              label={t('form.providerLabel')}
              value={form.providerId}
              onChange={(event) => {
                setForm((current) => ({ ...current, providerId: event.target.value }));
              }}
              required
            >
              {members.data.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.email} ({member.role})
                </option>
              ))}
            </SelectField>
          )}

          <Field
            label={t('form.appointmentTypeLabel')}
            value={form.appointmentType}
            onChange={(event) => {
              setForm((current) => ({ ...current, appointmentType: event.target.value }));
            }}
            maxLength={MAX_APPOINTMENT_TYPE_LENGTH}
            placeholder={t('form.appointmentTypePlaceholder')}
            required
          />

          <p className="m-0 text-sm text-ink-muted">{datetimeFormatHint}</p>
          <div className="flex flex-col gap-4 sm:flex-row">
            <Field
              label={t('form.startsLabel')}
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => {
                setForm((current) => ({ ...current, startsAt: event.target.value }));
              }}
              required
              className="sm:flex-1"
            />
            <Field
              label={t('form.endsLabel')}
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => {
                setForm((current) => ({ ...current, endsAt: event.target.value }));
              }}
              required
              className="sm:flex-1"
            />
          </div>

          <TextAreaField
            label={t('form.notesLabel')}
            value={form.notes}
            onChange={(event) => {
              setForm((current) => ({ ...current, notes: event.target.value }));
            }}
            maxLength={MAX_APPOINTMENT_NOTES_LENGTH}
            rows={3}
          />

          {formError !== null ? <ErrorText message={formError} /> : null}
          {mutationError !== null ? (
            isContractMismatch(mutationError) ? (
              <FailureNotice error={mutationError} />
            ) : (
              <ErrorText message={appointmentErrorMessage(mutationError)} />
            )
          ) : null}

          <div className="flex justify-end gap-3">
            <Button pending={pending} onClick={handleSubmit}>
              {pending
                ? t('form.saving')
                : isEdit
                  ? t('form.saveChanges')
                  : t('form.bookAppointment')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
